import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateProjectMutation } from '../../api/project';
import HookFormTextInput from '../Forms/TextInputs/HookFormTextInput';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../Store/modalSlice';
import { useNavigate } from "react-router-dom";
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaCircleCheck, FaCircleExclamation } from 'react-icons/fa6';
import { useProjectCreation } from '../../hooks/useProjectCreation';

let timer: any = null;

// Form validation schema
const projectSchema = z.object({
  name: z.string().min(1, "Project name is required").min(3, "Project name must be at least 3 characters"),
  description: z.string().optional(),
  tags: z.array(z.string().min(1, "Tag cannot be empty")).optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

export default function ProjectCreateForm() {
    const [createProject, { isLoading, isError }] = useCreateProjectMutation();
    const navigate = useNavigate();
    const {
        currentProjectCount,
        projectLimit,
        isProjectLimitReached
    } = useProjectCreation();
    
    const [projectCreated, setProjectCreated] = useState(false);
    const [tags, setTags] = useState<string[]>([]);
    const [currentTagInput, setCurrentTagInput] = useState('');
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<ProjectFormData>({
        resolver: zodResolver(projectSchema),
    });

    // Tag management functions
    const addTag = (tag: string) => {
        const trimmedTag = tag.trim();
        if (trimmedTag && !tags.includes(trimmedTag)) {
            const newTags = [...tags, trimmedTag];
            setTags(newTags);
            setValue('tags', newTags);
            setCurrentTagInput('');
        }
    };

    const removeTag = (tagToRemove: string) => {
        const newTags = tags.filter(tag => tag !== tagToRemove);
        setTags(newTags);
        setValue('tags', newTags);
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            if (currentTagInput.trim()) {
                addTag(currentTagInput);
            }
        }
    };

    const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Don't allow commas in the input
        if (!value.includes(',')) {
            setCurrentTagInput(value);
        }
    };

    useEffect(() => {
        return () => {
            clearTimeout(timer);
        };
    }, []);

    const onSubmit: SubmitHandler<ProjectFormData> = async (data) => {
        // Check if project limit is reached
        if (isProjectLimitReached) {
            console.error(`Project limit reached (${currentProjectCount}/${projectLimit}). Upgrade your plan to create more projects!`);
            return;
        }

        try {
            const payload = {
                name: data.name,
            } as any;

            // Add description if provided
            if (data.description && data.description.trim()) {
                payload.description = data.description;
            }

            // Add tags if provided (convert array to comma-separated string)
            if (data.tags && data.tags.length > 0) {
                payload.tags = data.tags.join(',');
            }

            const response = await createProject({ ...payload }).unwrap();
            console.log("Project created", response);

            setProjectCreated(true);
            timer = setTimeout(() => {
                dispatch(closeModal());
                // createProject invalidates 'Project', so the list refreshes itself.
                // The API responds with { message, project_id } — not `id`.
                navigate(`/projects/${response.project_id}`);
            }, 2000);

        } catch (error: any) {
            console.error('Failed to create project:', error);
        }
    };

    if (isLoading || isSubmitting) {
        return (
            <div className="flex justify-center items-center py-8">
                <LoadingSpiner />
                <span className="ml-2">Creating project...</span>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="alert alert-error">
                <FaCircleExclamation className="w-6 h-6" />
                <div>
                    <h3 className="font-bold">Error Creating Project</h3>
                    <div className="text-xs">There was an error creating your project. Please try again.</div>
                </div>
            </div>
        );
    }

    if (projectCreated) {
        return (
            <div className="alert alert-success">
                <FaCircleCheck className="w-6 h-6" />
                <div>
                    <h3 className="font-bold">Project Created Successfully!</h3>
                    <div className="text-xs">Redirecting to your new project...</div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <p className="text-base-content/70">
                    Create a new project to organize your images and analysis results.
                </p>
            </div>
            
            <form
                className="space-y-6"
                onSubmit={handleSubmit(onSubmit, (errors) => {
                    console.log('Form validation errors:', errors);
                })}
            >
            {/* Project Information Section */}
            <div className="form-section">
                <h3 className="text-lg font-semibold text-base-content mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Project Information
                </h3>
                <div className="grid grid-cols-1 gap-4">
                    <HookFormTextInput
                        name="name"
                        label="Project Name"
                        register={register}
                        errorMessage={errors.name?.message}
                        placeholder="Enter project name"
                        required
                    />
                    <div className="form-control w-full">
                        <label className="label" htmlFor="description">
                            <span className="label-text font-medium text-base-content">Description</span>
                            <div className="tooltip tooltip-left" data-tip="The description is used for organization, information sharing and search purposes">
                                <svg className="w-4 h-4 text-base-content/60 hover:text-base-content cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </label>
                        <input
                            {...register("description")}
                            type="text"
                            id="description"
                            placeholder="Enter project description"
                            className={`input input-bordered w-full ${errors.description ? 'input-error' : ''}`}
                        />
                        {errors.description && (
                            <label className="label">
                                <span className="label-text-alt text-error">{errors.description.message}</span>
                            </label>
                        )}
                    </div>
                    
                    {/* Tags Field */}
                    <div className="form-control w-full">
                        <label className="label" htmlFor="tags">
                            <span className="label-text font-medium text-base-content">Tags</span>
                            <div className="tooltip tooltip-left" data-tip="Tags are mainly used for filtering and organizing your data">
                                <svg className="w-4 h-4 text-base-content/60 hover:text-base-content cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </label>
                        
                        {/* Tags Display */}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="badge badge-primary badge-outline flex items-center gap-1"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => removeTag(tag)}
                                            className="ml-1 hover:text-error"
                                            aria-label={`Remove ${tag} tag`}
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        {/* Tag Input */}
                        <input
                            type="text"
                            id="tags"
                            value={currentTagInput}
                            onChange={handleTagInputChange}
                            onKeyDown={handleTagInputKeyDown}
                            placeholder="Type a tag and press Enter or Tab to add"
                            className={`input input-bordered w-full ${errors.tags ? 'input-error' : ''}`}
                        />
                        {errors.tags && (
                            <label className="label">
                                <span className="label-text-alt text-error">{errors.tags.message}</span>
                            </label>
                        )}
                        <label className="label">
                            <span className="label-text-alt text-base-content/60">Press Enter or Tab to add tags</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-base-300">
                <button
                    type="submit"
                    className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Creating...
                        </>
                    ) : (
                        'Create Project'
                    )}
                </button>
            </div>
            </form>
        </div>
    );
}
